/**
 * Profile analytics output intent (table vs figure).
 * Keep keyword lists in sync with app/backend/profile_analytics/output_intent.py
 */

export type AnalyticsOutputIntent = {
    wantTable: boolean;
    wantFigure: boolean;
};

/** Keywords that request a chart/diagram (en, sv, de + common typos). */
export const FIGURE_KEYWORDS: readonly string[] = [
    // English
    "diagram",
    "chart",
    "charts",
    "graph",
    "graphs",
    "plot",
    "plots",
    "plotting",
    "visualize",
    "visualise",
    "visualization",
    "visualisation",
    "figure",
    "figures",
    "illustration",
    "infographic",
    "histogram",
    "histograms",
    "scatter",
    "trend",
    "trends",
    "line chart",
    "line graph",
    "line plot",
    "bar chart",
    "bar graph",
    "bar plot",
    "pie chart",
    "pie graph",
    "pie plot",
    "donut chart",
    "doughnut chart",
    "area chart",
    "bubble chart",
    "heatmap",
    "heat map",
    "line",
    "lines",
    "bar",
    "bars",
    "pie",
    "circle",
    "circles",
    "donut",
    "doughnut",
    "staple", // common typo for bar / Swedish stapel
  // Swedish
    "diagram",
    "diagrammet",
    "diagrammet",
    "stapel",
    "stapeldiagram",
    "stapeldiagrammet",
    "linje",
    "linjediagram",
    "cirkel",
    "cirkeldiagram",
    "cirkeldiagrammet",
    "kurva",
    "kurvdiagram",
    "graf",
    "grafer",
    "grafen",
    "plotta",
    "plottning",
    "visualisera",
    "visualisering",
    "figur",
    "figurer",
    "histogram",
    "spridning",
    "spridningsdiagram",
    "trend",
    "trender",
    "cirkel",
    // German
    "diagramm",
    "diagramme",
    "diagramms",
    "stapel",
    "stapeldiagramm",
    "balken",
    "balkendiagramm",
    "linie",
    "liniendiagramm",
    "kreis",
    "kreisdiagramm",
    "torte",
    "tortendiagramm",
    "grafik",
    "grafiken",
    "graf",
    "grafen",
    "plotten",
    "visualisieren",
    "visualisierung",
    "abbildung",
    "abbildungen",
    "histogramm",
    "streuung",
    "streuungsdiagramm",
    "trend",
    "trends",
    "kurve",
    "kurven",
    "kuchen",
    "kuchendiagramm"
];

/** Keywords that request a table (en, sv, de). */
export const TABLE_KEYWORDS: readonly string[] = [
    // English
    "table",
    "tables",
    "tabular",
    "spreadsheet",
    "grid",
    "matrix",
    "list",
    "lists",
    "listing",
    "row",
    "rows",
    "column",
    "columns",
    "two-column",
    "two column",
    // Swedish
    "tabell",
    "tabeller",
    "tabellen",
    "tabellform",
    "lista",
    "listor",
    "listan",
    "rad",
    "rader",
    "kolumn",
    "kolumner",
    "rutnät",
    // German
    "tabelle",
    "tabellen",
    "tabellarisch",
    "liste",
    "listen",
    "zeile",
    "zeilen",
    "spalte",
    "spalten",
    "raster"
];

function escapeRegex(word: string): string {
    return word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function containsKeyword(query: string, keywords: readonly string[]): boolean {
    const normalized = query.toLowerCase();
    for (const keyword of keywords) {
        const pattern = new RegExp(`(?:^|[^\\p{L}\\p{N}])${escapeRegex(keyword.toLowerCase())}(?:[^\\p{L}\\p{N}]|$)`, "iu");
        if (pattern.test(normalized)) {
            return true;
        }
    }
    return false;
}

/**
 * Detect whether the user wants a table, a figure, or both from their query.
 * Default: table only when no keywords match.
 */
export function detectOutputIntent(query: string): AnalyticsOutputIntent {
    const hasFigureKeyword = containsKeyword(query, FIGURE_KEYWORDS);
    const hasTableKeyword = containsKeyword(query, TABLE_KEYWORDS);

    if (!hasFigureKeyword && !hasTableKeyword) {
        return { wantTable: true, wantFigure: false };
    }

    return {
        wantTable: hasTableKeyword,
        wantFigure: hasFigureKeyword
    };
}

export function figureDataUrl(base64: string): string {
    const trimmed = base64.trim();
    if (trimmed.startsWith("data:image/")) {
        return trimmed;
    }
    return `data:image/png;base64,${trimmed}`;
}

/** Map API snake_case intent to frontend camelCase. */
export function outputIntentFromApi(intent: {
    want_table: boolean;
    want_figure: boolean;
}): AnalyticsOutputIntent {
    return {
        wantTable: intent.want_table,
        wantFigure: intent.want_figure
    };
}
